import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment.dev';
import { SessionService } from './session';

type LiveEvent = 'create' | 'update' | 'enter' | 'leave' | 'delete';
type LiveCallback = (event: LiveEvent, object: any) => void;

@Injectable({ providedIn: 'root' })
export class LiveQueryService {
  private sessionService = inject(SessionService);

  private socket?: WebSocket;
  private isConnected = false;
  private isLiveQueryConnected = false;

  private listenersAttached = false;

  // Set by close(): stops the onclose/online handlers from re-opening a socket
  // that was deliberately torn down (logout, expired session).
  private closedByClient = false;

  // Active subscriptions (requestId -> callback)
  private subscriptions = new Map<number, LiveCallback>();

  // Pool of freed requestIds to reuse
  private freeIds: number[] = [];

  // Messages queued while socket isn't OPEN
  private pendingMessages: any[] = [];

  // Subscriptions queued before LiveQuery "connected"
  private pendingSubscriptions: {
    requestId: number;
    className: string;
    where: any;
    callback: LiveCallback;
  }[] = [];

  // Safety cap to avoid unbounded memory growth on flaky networks
  private readonly MAX_QUEUE = 500;

  constructor() {
    if (!this.listenersAttached) {
      window.addEventListener('offline', () => {
        console.warn('[LiveQuery] Browser went offline');
      });

      window.addEventListener('online', () => {
        if (this.closedByClient) return;
        console.log('[LiveQuery] Browser back online. Reconnecting…');
        this.connect();
      });

      this.listenersAttached = true;
    }
  }

  /**
   * Establish a WebSocket connection to the LiveQuery server
   * and send 'connect' once the socket is OPEN.
   * Multiple calls are safe (guarded).
   */
  connect(onOpen?: () => void, onReconnect?: () => void) {
    // A fresh connect() is an explicit re-open, so drop the teardown flag.
    this.closedByClient = false;

    // Guard: don't open another socket if we're already OPEN or CONNECTING
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      onOpen?.();
      return;
    }

    this.socket = new WebSocket(environment.wss);

    this.socket.onopen = () => {
      console.log('[LiveQuery] WebSocket opened');
      this.isConnected = true;
      this.isLiveQueryConnected = false;

      // Defer to next tick to ensure readyState is OPEN
      setTimeout(() => {
        this.send({
          op: 'connect',
          applicationId: 'ayn',
          sessionToken: this.sessionService.getSessionToken(),
        });
      }, 0);

      this.flushPendingMessages();
      onOpen?.();
    };

    this.socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      switch (data.op) {
        case 'connected':
          console.log('[LiveQuery] Connection acknowledged');
          this.isLiveQueryConnected = true;
          this.flushPendingSubscriptions();
          break;

        case 'subscribed':
          // optional: console.log(`[LiveQuery] Subscribed: ${data.requestId}`);
          break;

        case 'error':
          console.error('[LiveQuery] Error', data);
          break;

        default: {
          // Live events
          const isLiveEvent =
            data &&
            typeof data.op === 'string' &&
            (
              ['create', 'update', 'enter', 'leave', 'delete'] as LiveEvent[]
            ).includes(data.op);

          if (isLiveEvent) {
            const handler: LiveCallback | undefined =
              data.requestId !== null && data.requestId !== undefined
                ? this.subscriptions.get(data.requestId)
                : undefined;
            handler?.(data.op as LiveEvent, data.object);
          }
        }
      }
    };

    this.socket.onclose = () => {
      console.warn('[LiveQuery] Disconnected');
      this.isConnected = false;
      this.isLiveQueryConnected = false;

      // Deliberate teardown (logout / expired session): reconnecting here would
      // just re-authenticate the socket with a token the server has rejected.
      if (this.closedByClient) return;

      // Retry connection if online; otherwise wait for the next online event
      if (navigator.onLine) {
        console.log('[LiveQuery] Online - retrying in 2s');
        setTimeout(() => this.connect(undefined, onReconnect), 2000);
      } else {
        console.warn('[LiveQuery] Offline - waiting for reconnect');
        const handleOnline = () => {
          console.log('[LiveQuery] Reconnected to network, reconnecting…');
          window.removeEventListener('online', handleOnline);
          this.connect(undefined, onReconnect);
        };
        window.addEventListener('online', handleOnline);
      }

      onReconnect?.();
    };

    this.socket.onerror = (err) => {
      console.error('[LiveQuery] WebSocket error:', err);
    };
  }

  /**
   * Subscribe to a class with a filter and a callback.
   * Overloads:
   *   subscribeToClass(requestId, className, where, callback)
   *   subscribeToClass(className, where, callback) -> auto randomized requestId
   */
  subscribeToClass(
    requestId: number,
    className: string,
    where: any,
    callback: LiveCallback
  ): number;
  subscribeToClass(
    className: string,
    where: any,
    callback: LiveCallback
  ): number;
  subscribeToClass(
    a: number | string,
    b: string | any,
    c?: any,
    d?: LiveCallback
  ): number {
    let requestId: number;
    let className: string;
    let where: any;
    let callback: LiveCallback;

    if (typeof a === 'number') {
      // Old signature kept for compatibility
      requestId = a;
      className = b as string;
      where = c;
      callback = d!;
    } else {
      // Ergonomic signature: (className, where, callback)
      requestId = this.allocRequestId();
      className = a;
      where = b;
      callback = c;
    }

    if (this.isConnected && this.isLiveQueryConnected) {
      this._subscribe(requestId, className, where, callback);
    } else {
      this.pendingSubscriptions.push({ requestId, className, where, callback });
    }

    return requestId;
  }

  /**
   * Unsubscribe by requestId.
   */
  unsubscribe(requestId: number) {
    this.send({ op: 'unsubscribe', requestId });
    this.subscriptions.delete(requestId);
    this.releaseRequestId(requestId);
  }

  /**
   * Close and reset everything (e.g., on logout).
   */
  close() {
    this.closedByClient = true;
    this.pendingMessages = [];
    this.pendingSubscriptions = [];
    this.subscriptions.clear();
    try {
      this.socket?.close();
    } catch { /* ignored */ }
    this.socket = undefined;
    this.isConnected = false;
    this.isLiveQueryConnected = false;
  }

  // ----------------- Internals -----------------

  private _subscribe(
    requestId: number,
    className: string,
    where: any,
    callback: LiveCallback
  ) {
    this.subscriptions.set(requestId, callback);
    this.send({
      op: 'subscribe',
      requestId,
      sessionToken: this.sessionService.getSessionToken(),
      query: { className, where },
    });
  }

  private send(data: any) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
      return;
    }

    // Avoid duplicating 'connect'
    if (
      data.op === 'connect' &&
      this.pendingMessages.some((m) => m.op === 'connect')
    ) {
      return;
    }

    // Cap the queue to avoid memory blowups
    if (this.pendingMessages.length >= this.MAX_QUEUE) {
      this.pendingMessages.shift(); // drop oldest
    }
    this.pendingMessages.push(data);

    if (data.op !== 'connect') {
      console.warn('[LiveQuery] Socket not ready. Queued message:', data);
    }
  }

  private flushPendingMessages() {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    const msgs = this.pendingMessages.splice(0);
    for (const m of msgs) this.send(m);
  }

  private flushPendingSubscriptions() {
    if (!this.isLiveQueryConnected) return;
    const subs = this.pendingSubscriptions.splice(0);
    for (const s of subs)
      this._subscribe(s.requestId, s.className, s.where, s.callback);
  }

  // -------- RequestId generation (random, per-socket unique) --------
  private randomRequestId(): number {
    // Use crypto if available for uniform 31-bit positive ints
    if ('crypto' in globalThis && 'getRandomValues' in crypto) {
      const buf = new Uint32Array(1);
      crypto.getRandomValues(buf);
      return buf[0] & 0x7fffffff; // 0..2^31-1
    }
    // Fallback (still fine for per-socket uniqueness)
    return (Math.random() * 0x7fffffff) | 0;
  }

  private allocRequestId(): number {
    if (this.freeIds.length) return this.freeIds.pop()!;
    let id: number;
    do {
      id = this.randomRequestId();
    } while (this.subscriptions.has(id)); // ensure unique within this socket
    return id;
  }

  private releaseRequestId(id: number) {
    this.freeIds.push(id);
  }
}
